import moment from 'moment';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { Row, Col, Badge, Button, Tooltip, Space, Tabs } from 'antd';

import { Store } from '../../state/store.interfaces';
import { Breadcrumb } from '../AppHeader/interfaces';
import Icon from '../../common/components/Icon/Icon';
import { getCurrentTheme } from '../../helpers/theme';
import * as headerSlice from '../AppHeader/headerSlice';
import PublishStocking from '../Sowings/PublishStocking';
import { fetchVolumeRanges } from '../Units/campusSlice';
import * as stockingsSlice from '../Sowings/sowingsSlice';
import EditStockingModal from '../Sowings/EditStockingModal';
import MoveStockingModal from '../Sowings/MoveStockingModal';
import { LrvTag } from '../../common/components/LrvTag/LrvTag';
import FinishStockingModal from '../Sowings/FinishStockingModal';
import EnableStockingModal from '../Sowings/EnableStockingModal';
import { LrvTabs } from '../../common/components/LrvTabs/LrvTabs';
import { LrvText } from '../../common/components/LrvText/LrvText';
import IconButton from '../../common/components/buttons/IconButton';
import * as finishStockingSlice from '../Sowings/finishStockingSlice';
import { canBePublishedStocking } from '../../helpers/analysis.helpers';
import Breadcrumbs from '../../common/components/Breadcrumb/Breadcrumbs';
import { changeBreadcrumb, changeHeader } from '../AppHeader/headerSlice';
import { DropdownStockingOptions } from '../Sowings/DropdownStockingOptions';
import { FinishStockingInfoModal } from '../Sowings/FinishStockingInfoModal';
import { getTitleByStockingPhaseType } from '../../helpers/stocking.helpers';
import NotPermission from '../../common/components/NotPermission/NotPermission';
import { HarvestsStockingInfoModal } from '../Sowings/HarvestsStockingInfoModal';
import { TransferStockingInfoModal } from '../Sowings/TransferStockingInfoModal';
import { StockingParameterScreen } from '../StockingParameter/StockingParameterScreen';
import * as newStockingParameterSlice from '../StockingParameter/newStockingParameterSlice';
import * as detailStockingParameterSlice from '../StockingParameter/Detail/detailStockingParameterSlice';
import { StockingParameterDetailScreen } from '../StockingParameter/Detail/StockingParameterDetailScreen';
import { stockingPhaseTypes, isTradingRole, isParameteristRole } from '../../config/commons';

import './Sowing.scss';
import styles from './Sowing.module.scss';
import MoveAnalysis from './MoveAnalysis';
import * as analysisSlice from './sowingSlice';
import { AnalysisTable } from './AnalysisTable';
import { ParameterTable } from './ParameterTable';
import { EditAnalysisForm } from './EditAnalysisForm';
import * as stockingParameterSlice from './stockingParameterSlice';
import { AnalysisFromConsolidated } from './AnalysisFromConsolidated';

const { TabPane } = Tabs;

type TParams = { id: string };

const stockingTabs = {
  ANALYSES: 'ANALYSES',
  PARAMETERS: 'PARAMETERS',
};

function Analysis ({ match }: RouteComponentProps<TParams>) {
  const dispatch = useDispatch();
  const [t] = useTranslation();
  const stockingId = match.params.id;
  
  const [tabSelected, setTabSelected] = useState(isParameteristRole() ? stockingTabs.PARAMETERS : stockingTabs.ANALYSES);

  const {
    analysis,
    total,
    hasPermission,
    isDownloadingFile,
    currentPage,
    analysisCards,
  } = useSelector((state: Store) => state.analysis);

  const { selectedStocking: stocking, publishLarviaTrade } = useSelector((state: Store) => state.stockings);
  const { lastFourDaysParameters, parameterDate } = useSelector((state: Store) => state.newStockingParameter);
  const { breadcrumb, company: selectCompany, phaseType: phaseTypeGlobal } = useSelector((state: Store) => state.header);

  const { isLoadingToPublish } = publishLarviaTrade;
  const canPublishStocking = canBePublishedStocking(analysis);
  const stockingIsArchived = stocking.isArchived;

  const theme = getCurrentTheme();

  useEffect(() => {
    dispatch(changeHeader({ title: 'analysis.analysis' }));
    dispatch(fetchVolumeRanges());
    dispatch(stockingsSlice.fetchStockingDateRanges());
    dispatch(finishStockingSlice.fetchStockingFinishedData());
  }, [dispatch]);

  useEffect(() => {
    if (!stocking.name) {
      return;
    }

    const breadcrumb: Breadcrumb[] = [{
      id: 'sowings',
      name: getTitleByStockingPhaseType(phaseTypeGlobal),
      url: '/production/stockings',
    },
    {
      id: stocking.name,
      name: `${t('stockings.stocking')} ${stocking.name}`,
      url: `/production/stockings/${stockingId}`,
    }];
    dispatch(changeBreadcrumb(breadcrumb));
  }, [dispatch, stockingId, stocking.name, phaseTypeGlobal]);

  useEffect(() => {
    dispatch(stockingsSlice.fetchStocking({ id: stockingId }));
  }, [dispatch, stockingId]);

  useEffect(() => {
    if (!stocking.companyId || stocking.companyId === selectCompany._id) {
      return;
    }

    dispatch(headerSlice.fetchCompany(stocking.companyId));
  }, [dispatch, stocking.companyId, selectCompany._id]);

  useEffect(() => {
    if (stocking.phaseType === phaseTypeGlobal) {
      return;
    }

    dispatch(headerSlice.setPhaseType(stocking.phaseType));
  }, [dispatch, phaseTypeGlobal, stocking.phaseType]);

  useEffect(() => {
    if (!stocking.companyId) {
      return;
    }

    if (!isParameteristRole()) {
      dispatch(analysisSlice.fetchAnalysis({ page: 0, stockingId, companyId: stocking.companyId }));
    }

    dispatch(stockingParameterSlice.fetchMergedStockingParameters({ stockingId, companyId: stocking.companyId, unitId: stocking.campusId._id }));
    dispatch(stockingParameterSlice.fetchStockingParameterCustom({ companyId: stocking.companyId }));
    dispatch(stockingParameterSlice.fetchCompanyStockingParameter({ companyId: stocking.companyId }));
  }, [dispatch, stockingId, stocking.companyId, stocking.phaseType]);

  useEffect(() => {
    if (!stocking?._id) {
      return;
    }

    dispatch(analysisSlice.fetchCompanySamplingTypes({ phaseType: stocking.phaseType }));
  }, [dispatch, stocking.phaseType]);

  useEffect(() => {
    if (isParameteristRole() || !stocking.companyId || analysisCards.length > 0) {
      return;
    }

    dispatch(analysisSlice.fetchCustomAnalysisCards({ companyId: stocking.companyId }));
  }, [dispatch, stockingId, stocking.companyId, analysisCards.length]);


  if (!hasPermission) {
    return <NotPermission />;
  }

  useEffect(() => {
    if (!lastFourDaysParameters.length || !parameterDate) {
      return;
    }

    const paramDate = moment(parameterDate).startOf('day');
    const parameterFound = lastFourDaysParameters.find(item => moment(item.date).startOf('day').isSame(paramDate));

    if (!parameterFound) {
      dispatch(detailStockingParameterSlice.setStockingParameterSelected(undefined));
      return;
    }
    dispatch(detailStockingParameterSlice.fetchCompanyStockingParameters({ companyId: selectCompany._id }));
    dispatch(detailStockingParameterSlice.fetchStockingParameter({ _id: parameterFound._id }));
  }, [parameterDate, lastFourDaysParameters, selectCompany]);

  const onClickAddParameterButton = () => {
    dispatch(newStockingParameterSlice.fetchLastFourDaysParameters({ companyId: selectCompany._id, stockingId }));
    dispatch(newStockingParameterSlice.showStockingParameterModal({ companyId: selectCompany._id }));
  };

  const renderBindingCode = () => {
    if (isParameteristRole()) {
      return null;
    }
    
    if (!stocking.stockingBindingCode) {
      return null;
    }

    return (
      <div>
        <LrvText text={`${t('stockings.pdf.stockingBindingCode')}: `} theme={theme} />
        <LrvText text={stocking.stockingBindingCode} theme={theme} />
      </div>
    );
  };

  const renderParameterButton = () => {
    if (!selectCompany.showStockingParameterSection || tabSelected !== stockingTabs.PARAMETERS) {
      return;
    }

    return (
      <Button
        id='publish_stocking_button'
        type='primary'
        className={styles.button}
        icon={<Icon name='add' />}
        onClick={onClickAddParameterButton}
      >
        &nbsp;{t('stockingParameter.parameter')}
      </Button>
    );
  };

  const renderPublishButton = () => {
    if (!selectCompany.isInternational && isTradingRole() && stocking.phaseType === stockingPhaseTypes.LARVAE) {
      const titleTooltip = canPublishStocking ? undefined : t('stockings.trade.cannotPublish');
      const labelButton = stocking.isPublic ? t('stockings.trade.remove') : t('stockings.trade.publish');

      return (
        <Tooltip id='tooltip_publish_stocking' title={titleTooltip}>
          <Button
            id='publish_stocking_button'
            type='primary'
            className={styles.button}
            icon={<Icon name='share-box' />}
            disabled={!canPublishStocking}
            loading={stocking.isPublic ? isLoadingToPublish : undefined}
            onClick={() => {
              if (stocking.isPublic) {
                dispatch(stockingsSlice.publishStocking({ stockingId, isPublic: !stocking.isPublic }));
              } else {
                dispatch(stockingsSlice.setShowPublishStockingModal(true));
              }
            }}
          >&nbsp;{labelButton}</Button>
        </Tooltip>
      );
    }
    return null;
  };

  const generatePdf = () => {
    if (!stocking?._id || !stocking?.name) {
      return;
    }

    dispatch(analysisSlice.getUrlStockingChartsPdf(stocking._id));
  };

  const renderGeneratePdf = () => {
    if (isParameteristRole()) {
      return null;
    }

    return (
      <IconButton
        loading={isDownloadingFile}
        disabled={!stocking?._id}
        iconName='download'
        onClick={generatePdf}
        tooltipText={t('production.download')}
      />
    );
  };

  const renderSubHeader = () => {
    if (!stocking._id) {
      return null;
    }

    if (stockingIsArchived) {
      return (
        <Row className={styles.rowHeader} align='middle' justify='space-between'>
          <Space className={styles.archivedTag} align='end' size='middle'>
            <div>
              <LrvText text={stocking.name} theme={theme} />
            </div>
            <LrvTag color='#f50'>{t('detail.archived')}</LrvTag>
          </Space>
        </Row>
      );
    }

    return (
      <Row className={styles.rowHeader} align='middle' justify='space-between'>
        <Breadcrumbs breadcrumb={breadcrumb} noCapitalize theme={theme} />
        <Space className={styles.row} size='middle'>
          {renderBindingCode()}
          {renderGeneratePdf()}
          {renderPublishButton()}
          {renderParameterButton()}
          <DropdownStockingOptions stocking={stocking} theme={theme} page='sowing' />
        </Space>
      </Row>
    );
  };

  const renderNotShowingAnalysisLabel = () => {
    if (isParameteristRole()) {
      return null;
    }

    if (total >= stocking.analysisCount) {
      return null;
    }

    return (
      <Row align='middle' className={styles.analysisCount}>
        <Space>
          <Badge color='#2db7f5' />
          <LrvText
            theme={theme}
            text={t('analysis.notShowing', { number: stocking.analysisCount - total })}
          />
        </Space>
      </Row>
    );
  };

  const renderTabs = () => {
    return (
      <LrvTabs
        theme={theme}
        tabPosition='top'
        onChange={setTabSelected}
        defaultActiveKey={tabSelected}
      >
        {
          !isParameteristRole() &&
          <TabPane
            key={stockingTabs.ANALYSES}
            tab={<div id='tab_analyses'>{t('stockings.analyses')}</div>}
          >
            <AnalysisTable stockingId={stockingId} />
          </TabPane>
        }
        
        {
          selectCompany.showStockingParameterSection &&
          <TabPane
            key={stockingTabs.PARAMETERS}
            tab={<div id='tab_parameters'>{t('stockings.parameters')}</div>}
          >
            <ParameterTable />
          </TabPane>
        }
      </LrvTabs>
    );
  };

  return (
    <div className={styles.analysis}>
      <Row className={cx('analysisSection', total >= stocking.analysisCount ? '' : 'analysisSectionWithLabel', 'analysisSectionWithParameterOptions')}>
        <Col className={styles.columnFlex} >
          {renderSubHeader()}
          {renderNotShowingAnalysisLabel()}
          {renderTabs()}
        </Col>
      </Row>

      <EditAnalysisForm stockingId={stockingId}/>

      <FinishStockingInfoModal theme='light' />
      <TransferStockingInfoModal theme='light' />
      <HarvestsStockingInfoModal theme='light' />
      <MoveAnalysis theme='light' />
      <EditStockingModal theme='light' />
      <MoveStockingModal theme='light' />
      <FinishStockingModal theme='light' />
      <EnableStockingModal theme='light' />

      <PublishStocking stocking={stocking} company={selectCompany} />
      <AnalysisFromConsolidated currentPage={currentPage} />
      <StockingParameterScreen />
      <StockingParameterDetailScreen />
    </div>
  );
}

export default Analysis;
